import { FC, useEffect, useRef, useState, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import { CircularProgress } from '@mui/material';

import { useContractChargesDetailsForShippingService } from 'src/use-cases/invoice/useNoContract';
import { TableShippingService } from './TableShippingService';
import { INoContractContentStep2Props, NoContractForm, SelectedShippingServiceItem, GroupedShippingService } from './types';
import { NoContractSectionHeader } from './SectionHeader';
import { TableChargesUnified } from './TableChargesUnified';

/**
 * Maps shipping services directly from backend structure without re-grouping
 * Each SelectedShippingServiceItem becomes one GroupedShippingService
 */
const mapShippingServices = (data: SelectedShippingServiceItem[]): GroupedShippingService[] =>
  data
    .filter((item) => item.existingShippingService && item.contractInfos && item.contractInfos.length > 0)
    .map((item, index) => {
      const service = item.existingShippingService!;

      return {
        // Use index + route as unique key
        key: `${index}-${service.route || 'unknown'}`,

        // Service fields from existingShippingService
        shipperName: service.shipperName,
        route: service.route,
        consigneeName: service.consigneeName,
        billingPartyCode: service.billingPartyCode,
        modality: service.modality,
        service: service.service,
        type: service.type,
        carrier: service.carrier,
        incoterm: service.incoterm,

        // Service-level mismatches (from PartialMatchingServiceDto)
        serviceMismatchedFields: item.mismatchedFields,

        // Variants from contractInfos (different validity periods)
        variants: item.contractInfos.map((contractInfo) => ({
          contractFlowId: contractInfo.contractFlowId!,
          validityPeriod: {
            start: new Date(contractInfo.startDate),
            end: new Date(contractInfo.endDate),
          },
          startDate: contractInfo.startDate!,
          endDate: contractInfo.endDate!,
          listZone: contractInfo.listZone,
          mismatchedFields: contractInfo.mismatchedFields, // Contract-level mismatches
        })),

        // Store original item for draft saving
        originalItem: item,
      };
    });

/**
 * Component for creating the content of step 2 in the no contract flow.
 *
 * @param {INoContractContentStep2Props} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
export const NoContractContentStep2Create: FC<INoContractContentStep2Props> = (props) => {
  const { isEditable, matchingService } = props;
  const { watch, setValue } = useFormContext<NoContractForm>();
  const { contractChoice } = watch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showContent, setShowContent] = useState(false);
  const [animationState, setAnimationState] = useState<'enter' | 'exit'>('enter');
  const [drawerHeight, setDrawerHeight] = useState(52); // Default 52vh (100vh - 48vh)
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartY = useRef(0);
  const resizeStartHeight = useRef(0);

  console.log('contractChoice', contractChoice);

  // Map shipping services directly from backend structure
  const groupedServices = useMemo(() => mapShippingServices(matchingService.partialMatchingServices || []), [matchingService.partialMatchingServices]);

  // Track selected group
  const [selectedGroup, setSelectedGroup] = useState<GroupedShippingService | null>(null);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number>(0);

  // Initialize selected group when contractChoice exists on load
  useEffect(() => {
    if (contractChoice && groupedServices.length > 0 && !selectedGroup) {
      // Find the group that contains this contractChoice
      for (const group of groupedServices) {
        const variantIndex = group.variants.findIndex((v) => v.contractFlowId === contractChoice);
        if (variantIndex !== -1) {
          setSelectedGroup(group);
          setSelectedVariantIndex(variantIndex);
          break;
        }
      }
    }
  }, [contractChoice, groupedServices, selectedGroup]);

  // Fetch charges data when a contract is selected
  const { data: chargesData, isLoading: isLoadingCharges } = useContractChargesDetailsForShippingService(contractChoice || null);

  // Update form data when charges data changes
  useEffect(() => {
    if (chargesData?.containerCharges) {
      const items = chargesData.containerCharges.filter((check) => check?.chargeType);
      setValue('containerCharges', items);
    }
    if (chargesData?.declarationCharges) {
      const items = chargesData.declarationCharges.filter((check) => check?.chargeType);
      setValue('declarationCharges', items);
    }
    if (chargesData?.shipmentCharges) {
      const items = chargesData.shipmentCharges.filter((check) => check?.chargeType);
      setValue('shipmentDeclarationCharges', items);
    }
    if (chargesData?.percentageCharges) {
      const items = chargesData.percentageCharges
        .filter((check) => check?.chargeType)
        .map((item) => {
          const newItem = {
            ...item,
            validityPeriod: {
              start: item?.startDate ? new Date(item?.startDate) : undefined,
              end: item?.endDate ? new Date(item?.endDate) : undefined,
            },
          };
          return newItem;
        });
      setValue('percentageCharges', items);
    }
    if (chargesData?.weightCharges) {
      const items = chargesData.weightCharges.filter((check) => check?.chargeType);
      setValue('weightCharges', items);
    }
  }, [chargesData, setValue]);

  // CSS for the animation
  const animationStyles = `
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(100px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes fadeOutDown {
      from {
        opacity: 1;
        transform: translateY(0);
      }
      to {
        opacity: 0;
        transform: translateY(100px);
      }
    }

    .slide-container-enter {
      animation: fadeInUp 0.4s ease forwards;
    }

    .slide-container-exit {
      animation: fadeOutDown 0.4s ease forwards;
    }
  `;

  // Handle resize drag events
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartY.current = e.clientY;
    resizeStartHeight.current = drawerHeight;
  };

  // Handle keyboard resize
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const step = 5; // 5vh per key press
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setDrawerHeight((prev) => Math.min(prev + step, 80));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setDrawerHeight((prev) => Math.max(prev - step, 20));
    }
  };

  useEffect(() => {
    const handleResizeMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaY = resizeStartY.current - e.clientY; // Negative when dragging down, positive when dragging up
      const viewportHeight = window.innerHeight;
      const deltaVh = (deltaY / viewportHeight) * 100;
      const newHeight = Math.min(Math.max(resizeStartHeight.current + deltaVh, 20), 80); // Min 20vh, Max 80vh

      setDrawerHeight(newHeight);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing]);

  // Handle entrance and exit animations
  useEffect(() => {
    if (contractChoice) {
      setAnimationState('enter'); // This was missing!
      setShowContent(true);

      // Allow a small delay for the drawer to start showing
      setTimeout(() => {
        // Find the selected row by attribute
        const selectedRow = document.querySelector('[data-row-selected="true"]');

        if (selectedRow) {
          // Simple scroll to center the selected row smoothly
          selectedRow.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 300);
    } else if (showContent) {
      setAnimationState('exit');
      setTimeout(() => {
        setShowContent(false);
      }, 500);
    }
  }, [contractChoice, showContent]);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
        ${animationStyles}
      `,
        }}
      />
      <div className={clsx(isEditable ? 'p-24' : 'mt-48')}>
        <NoContractSectionHeader
          title='Copy Charges from an Existing Shipping Service'
          description='Please choose one shipping service and validity period to copy from'
        />

        <div className='mt-24'>
          <TableShippingService
            data={groupedServices}
            onSelectGroup={(group) => {
              setSelectedGroup(group);
              setSelectedVariantIndex(0);
              // Set the first variant's contractFlowId
              setValue('contractChoice', group.variants[0].contractFlowId);
            }}
          />
        </div>
      </div>

      {showContent && (
        <div
          ref={containerRef}
          className={`absolute z-[10] left-0 right-0 bg-white border-t border-neutral-20 shadow-[0_-4px_12px_0_rgba(0,0,0,0.1)] ${
            animationState === 'enter' ? 'slide-container-enter' : 'slide-container-exit'
          }`}
          style={{
            bottom: 0,
            height: `${drawerHeight}vh`,
            transition: isResizing ? 'none' : 'height 0.2s ease',
          }}
        >
          {/* Resize Handle */}
          <button
            type='button'
            aria-label='Resize charges panel. Use arrow up and down keys to adjust height.'
            aria-valuenow={drawerHeight}
            aria-valuemin={20}
            aria-valuemax={80}
            onMouseDown={handleResizeStart}
            onKeyDown={handleKeyDown}
            className={`absolute top-0 left-0 right-0 h-[6px] cursor-ns-resize hover:bg-blue-500/20 transition-colors group border-0 bg-transparent p-0 ${
              isResizing ? 'bg-blue-500/30' : ''
            }`}
            style={{ zIndex: 100 }}
          >
            <div className='absolute top-[2px] left-1/2 transform -translate-x-1/2 w-[40px] h-[3px] bg-neutral-30 rounded-full group-hover:bg-blue-500 transition-colors pointer-events-none' />
          </button>
          {isLoadingCharges && (
            <div className='flex items-center justify-center p-48'>
              <CircularProgress color='primary' />
            </div>
          )}
          {!isLoadingCharges && chargesData && selectedGroup && (
            <TableChargesUnified
              data={chargesData}
              selectedGroup={selectedGroup}
              selectedVariantIndex={selectedVariantIndex}
              onVariantChange={(index) => {
                setSelectedVariantIndex(index);
                setValue('contractChoice', selectedGroup.variants[index].contractFlowId);
              }}
              onClose={() => {
                setShowContent(false);
                setSelectedGroup(null);
                setValue('contractChoice', null);
                // Reset charges data
                setValue('containerCharges', []);
                setValue('shipmentDeclarationCharges', []);
                setValue('weightCharges', []);
                setValue('percentageCharges', []);
              }}
            />
          )}
        </div>
      )}
    </>
  );
};
